import React, { useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { DictionaryIrregularVerb } from 'app/types';
import CategoryRail, { RailItem } from 'pages/WordSet/CategoryRail';
import UIBox from 'components/ui/UIBox';
import UIText from 'components/ui/UIText';
import { aggregateByBase } from './aggregations';
import { Selection } from './urlState';
import { dieslova } from './patternSuperGroups';
import DrillHeader from './DrillHeader';
import IrregularVerbsTable from './IrregularVerbsTable';
import PatternRow from './PatternRow';

interface Props {
  verbs: readonly DictionaryIrregularVerb[];
  selection: Selection | null;
  onSelectBase: (baseVerb: string) => void;
  onClearSelection: () => void;
}

// Same brand purple shared with /wordset/level-based and the other
// irregular-verb modes — keeps the whole area on one accent.
const FAMILY_ACCENT = '#3B2C93';

const buildFamilyLink = (baseVerb: string) =>
  `/wordset/irregular-verb/family/${encodeURIComponent(baseVerb)}`;

// Section anchor / rail id per letter. Prefix keeps the namespace separate
// from pattern super-groups so a future shared-rail refactor stays safe.
const letterSlug = (letter: string) => `f-${letter.toLowerCase()}`;

const GroupHead: React.FC<{
  color: string;
  title: string;
  count: number;
}> = ({ color, title, count }) => (
  <UIBox
    sx={{
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'nowrap',
      gap: 1.5,
      mb: 1,
    }}
  >
    <UIBox
      sx={{
        bgcolor: color,
        borderRadius: '50%',
        flex: 'none',
        height: 10,
        width: 10,
      }}
    />
    <UIText
      sx={{
        color: 'primary.subtitle',
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {title}
    </UIText>
    <UIText
      sx={{
        bgcolor: 'background.default',
        borderRadius: 999,
        color: 'primary.subtitle',
        fontSize: 12,
        fontWeight: 700,
        px: 1.1,
        py: 0.25,
        whiteSpace: 'nowrap',
      }}
    >
      {`${count} ${dieslova(count)}`}
    </UIText>
    <UIBox sx={{ bgcolor: 'divider', flex: 1, height: '1px' }} />
  </UIBox>
);

const FamiliesMode: React.FC<Props> = ({
  verbs,
  selection,
  onSelectBase,
  onClearSelection,
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // onSelectBase stays in the public Prop API for parity with the other
  // modes, but Families now uses Link-based navigation via PatternRow.
  void onSelectBase;

  const agg = aggregateByBase(verbs);

  // Group bases (with ≥ 2 compounds, matching the prior overview rule) by
  // first letter, alphabetically. Each letter becomes a rail target.
  // Hooks must run unconditionally, so they stay above the early return below.
  const groupsByLetter = useMemo(() => {
    const families = Array.from(agg.entries())
      .map(([base, a]) => ({ base, ...a }))
      .filter((f) => f.total >= 2)
      .sort((a, b) => a.base.localeCompare(b.base));

    const buckets = new Map<
      string,
      Array<{ base: string; total: number; learned: number; added: number }>
    >();
    for (const f of families) {
      const letter = (f.base[0] ?? '#').toUpperCase();
      const arr = buckets.get(letter) ?? [];
      arr.push(f);
      buckets.set(letter, arr);
    }
    return Array.from(buckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([letter, items]) => ({
        letter,
        slug: letterSlug(letter),
        items,
        total: items.reduce((s, i) => s + i.total, 0),
      }));
  }, [agg]);

  const railItems = useMemo<RailItem[]>(
    () => [
      {
        kind: 'group',
        label: '',
        children: groupsByLetter.map((g) => ({
          id: g.slug,
          title: g.letter,
          accent: FAMILY_ACCENT,
        })),
      },
    ],
    [groupsByLetter],
  );

  if (selection?.kind === 'base') {
    const filtered = verbs.filter((v) => v.baseVerb === selection.value);
    return (
      <Box>
        <DrillHeader
          backLabel="До списку сімейств"
          title={`Сімейство ${selection.value}`}
          count={filtered.length}
          onBack={onClearSelection}
        />
        <IrregularVerbsTable verbs={filtered} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, fontSize: 14, color: 'text.secondary' }}>
        Базові дієслова з ≥ 2 похідними. Натисни, щоб побачити всю родину форм.
      </Box>
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 0, md: 4 },
          gridTemplateColumns: { xs: '1fr', md: '232px 1fr' },
        }}
      >
        <CategoryRail
          items={railItems}
          hoveredId={hoveredId}
          onHover={setHoveredId}
          dimInactive
        />

        <Stack spacing={3.5}>
          {groupsByLetter.map((g) => (
            <Box
              key={g.slug}
              id={`ws-${g.slug}`}
              onMouseEnter={() => setHoveredId(g.slug)}
              onMouseLeave={() => setHoveredId(null)}
              sx={{ scrollMarginTop: { xs: 128, md: 80 } }}
            >
              <GroupHead
                color={FAMILY_ACCENT}
                title={g.letter}
                count={g.total}
              />
              <Stack spacing={1}>
                {g.items.map((f, idx) => (
                  <PatternRow
                    key={f.base}
                    index={idx}
                    title={f.base}
                    subtitle={`${f.total} похідних`}
                    total={f.total}
                    learned={f.learned}
                    added={f.added}
                    color={FAMILY_ACCENT}
                    link={buildFamilyLink(f.base)}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default FamiliesMode;
